import type { WorkspaceNodeType } from "../../../types/workspace";

export const WORKSPACE_HUB_PATH = "/workspace";

/** Location state when opening strategy / report / simulation pages from the hub. */
export type WorkspaceHubNavState = {
  fromWorkspace: true;
};

export const WORKSPACE_HUB_BREADCRUMB = {
  label: "Workspace",
  to: WORKSPACE_HUB_PATH,
} as const;

export function isWorkspaceHubNavState(
  state: unknown
): state is WorkspaceHubNavState {
  return Boolean(
    state &&
      typeof state === "object" &&
      (state as { fromWorkspace?: unknown }).fromWorkspace === true
  );
}

const WORKSPACE_NODE_DEEP_LINK_TYPES = new Set<WorkspaceNodeType>([
  "account",
  "strategy",
  "report",
  "simulation",
  "policy",
  "combined_policy",
  "combined_simulation",
]);

export type WorkspaceNodeDeepLink = {
  type: WorkspaceNodeType;
  entityId: string;
};

export type WorkspaceCanvasPathOptions = {
  /** When set, opens the canvas with this entity’s drawer focused (`?node=type/entityId`). */
  node?: WorkspaceNodeDeepLink | null;
};

/** Serialize `?node=type/entityId` (URLSearchParams encodes the value). */
export function formatWorkspaceNodeSearchValue(
  type: WorkspaceNodeType,
  entityId: string
): string {
  return `${type}/${entityId.trim()}`;
}

/** Parse `?node=:entityType/:entityId` from a location search string. */
export function parseWorkspaceNodeSearchParam(
  search: string
): WorkspaceNodeDeepLink | null {
  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search
  );
  const raw = params.get("node")?.trim();
  if (!raw) return null;

  const slash = raw.indexOf("/");
  if (slash <= 0 || slash >= raw.length - 1) return null;

  const type = raw.slice(0, slash).trim() as WorkspaceNodeType;
  const entityId = raw.slice(slash + 1).trim();
  if (!entityId || !WORKSPACE_NODE_DEEP_LINK_TYPES.has(type)) return null;

  return { type, entityId };
}

/** Merge or clear the `node` query param on an existing search string. */
export function withWorkspaceNodeSearch(
  search: string,
  node: WorkspaceNodeDeepLink | null
): string {
  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search
  );
  if (!node?.entityId.trim()) {
    params.delete("node");
  } else {
    params.set(
      "node",
      formatWorkspaceNodeSearchValue(node.type, node.entityId)
    );
  }
  const next = params.toString();
  return next ? `?${next}` : "";
}

export function workspaceCanvasPath(
  workspaceId: string,
  options?: WorkspaceCanvasPathOptions
): string {
  const base = `/workspace/${encodeURIComponent(workspaceId)}`;
  const node = options?.node;
  if (!node?.entityId.trim()) return base;
  return `${base}${withWorkspaceNodeSearch("", node)}`;
}

/** Strategy federated canvas (`/workspace/strategy/:strategyId`). */
export function strategyWorkspacePath(
  strategyId: string,
  options?: WorkspaceCanvasPathOptions
): string {
  const base = `/workspace/strategy/${encodeURIComponent(strategyId.trim())}`;
  const node = options?.node;
  if (!node?.entityId.trim()) return base;
  return `${base}${withWorkspaceNodeSearch("", node)}`;
}

/** True for `/workspace/strategy/:strategyId`. */
export function isStrategyWorkspacePathname(pathname: string): boolean {
  const path = pathname.split("?")[0]?.replace(/\/+$/, "") || "";
  return path.startsWith(`${WORKSPACE_HUB_PATH}/strategy/`) && path.length > `${WORKSPACE_HUB_PATH}/strategy/`.length;
}

/** True for `/workspace/:workspaceId` (not the hub list, not strategy federated). */
export function isWorkspaceCanvasPathname(pathname: string): boolean {
  const path = pathname.split("?")[0]?.replace(/\/+$/, "") || "";
  if (path === WORKSPACE_HUB_PATH) return false;
  if (isStrategyWorkspacePathname(path)) return false;
  return path.startsWith(`${WORKSPACE_HUB_PATH}/`) && path.length > WORKSPACE_HUB_PATH.length + 1;
}

/** Account or Strategy canvas (not hub). */
export function isAnyWorkspaceCanvasPathname(pathname: string): boolean {
  return isWorkspaceCanvasPathname(pathname) || isStrategyWorkspacePathname(pathname);
}
